import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, ArrowLeft, Trash2, Library as LibraryIcon, Award, Flame, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '@/components/Footer';
import Avatar from '@/components/Avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Library({ user, setUser }) {
  const navigate = useNavigate();
  const [devotionals, setDevotionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });

  useEffect(() => {
    loadLibrary();
  }, []);

  const loadLibrary = async () => {
    try {
      const [libResp, badgesResp, streakResp] = await Promise.all([
        axios.get(`${API}/devotionals/library`),
        axios.get(`${API}/badges`),
        axios.get(`${API}/streak`)
      ]);
      setDevotionals(libResp.data);
      setBadges(badgesResp.data);
      setStreak(streakResp.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (devotionalId) => {
    try {
      await axios.delete(`${API}/devotionals/${devotionalId}`);
      setDevotionals(devotionals.filter(d => d.id !== devotionalId));
      toast.success('Removed from library');
    } catch (err) {
      toast.error('Failed to remove');
      console.error(err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const BADGE_INFO = {
    'first_reading': { name: 'First Reading', icon: '📖' },
    '5_day_streak': { name: '5-Day Streak', icon: '🔥' },
    '10_day_streak': { name: '10-Day Streak', icon: '⭐' },
    '30_day_streak': { name: '30-Day Streak', icon: '🏆' },
    '10_saved': { name: '10 Saved', icon: '❤️' },
    '50_readings': { name: '50 Readings', icon: '🌟' }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <div className="cosmic-loader"></div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24">
      <div className="fade-in max-w-[1200px] mx-auto pb-20">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="back-button"
              variant="ghost"
              className="p-2 hover:bg-gold/10"
            >
              <ArrowLeft size={24} className="text-gold" />
            </Button>
            <LibraryIcon size={48} className="text-gold" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase m-0">Your Library</h1>
          </div>

          <div className="flex items-center gap-4">
            <div
              className="flex items-center gap-3 px-4 py-2 bg-black/80 rounded-md border border-gold/30 cursor-pointer hover:border-gold/50 transition-colors"
              onClick={() => navigate('/profile')}
            >
              <Avatar
                picture={user?.picture}
                name={user?.name}
                size={32}
              />
              <span className="text-gold font-medium">{user?.name}</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-12">
          <div className="mystical-card p-6 text-center">
            <Flame size={40} className="text-gold mx-auto mb-2" />
            <h2 className="text-4xl font-bold my-2">{streak.current_streak}</h2>
            <p className="text-gray-400 uppercase tracking-widest text-sm">Current Streak</p>
            <p className="text-gray-600 text-xs mt-2 uppercase">Longest: {streak.longest_streak} days</p>
          </div>

          <div className="mystical-card p-6 text-center">
            <Award size={40} className="text-gold mx-auto mb-2" />
            <h2 className="text-4xl font-bold my-2">{badges.length}</h2>
            <p className="text-gray-400 uppercase tracking-widest text-sm">Badges Earned</p>
          </div>

          <div className="mystical-card p-6 text-center">
            <LibraryIcon size={40} className="text-gold mx-auto mb-2" />
            <h2 className="text-4xl font-bold my-2">{devotionals.length}</h2>
            <p className="text-gray-400 uppercase tracking-widest text-sm">Saved Readings</p>
          </div>
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="mystical-card p-8 mb-8">
            <h2 className="text-2xl font-bold mb-6 uppercase tracking-wider">Your Achievements</h2>
            <div className="flex flex-wrap gap-4">
              {badges.map((badge, idx) => {
                const info = BADGE_INFO[badge.badge_type] || { name: badge.badge_type, icon: '🏅' };
                return (
                  <div key={idx} className="badge bg-gold/5 border border-gold/20 px-4 py-2 rounded-lg flex items-center gap-3" data-testid={`badge-${badge.badge_type}`}>
                    <span className="text-2xl">{info.icon}</span>
                    <div>
                      <div className="font-bold text-sm text-gold uppercase">{info.name}</div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-tighter">
                        {formatDate(badge.earned_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Saved Devotionals */}
        {devotionals.length === 0 ? (
          <div className="mystical-card text-center p-16">
            <BookOpen size={64} className="text-gray-600 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4 text-gray-400">Your library is empty</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Save devotionals that resonate with your soul during your daily contemplations.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="start-reading-button"
              className="btn-gold px-8 py-6 text-lg whitespace-normal md:whitespace-nowrap"
            >
              Generate Your First Reading
            </Button>
          </div>
        ) : (
          <div className="mb-24">
            <h2 className="text-2xl font-bold mb-6 uppercase tracking-widest border-b border-gold/20 pb-4">
              Saved Devotionals ({devotionals.length})
            </h2>
            <div className="flex flex-col gap-6">
              {devotionals.map((devotional, idx) => (
                <div
                  key={devotional.id}
                  data-testid={`devotional-${idx}`}
                  className="mystical-card p-8 fade-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="flex flex-wrap justify-between items-start gap-4 mb-6 border-b border-gold/10 pb-6">
                    <div className="flex-1">
                      <p className="text-xs text-gold font-bold uppercase tracking-widest mb-2">
                        {formatDate(devotional.created_at)}
                      </p>
                      <h3 className="text-2xl md:text-3xl font-bold italic tracking-tight m-0 text-[#e8e8e8]">
                        {devotional.topic}
                      </h3>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {devotional.lenses_used.map((lens, i) => (
                          <span key={i} className="badge px-3 py-1 text-[10px] uppercase font-bold tracking-widest">
                            {lens}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => navigate(`/devotional/${devotional.id}`)}
                        data-testid={`view-${idx}`}
                        variant="ghost"
                        className="p-2 hover:bg-gold/10"
                      >
                        <Eye size={22} className="text-gold" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(devotional.id)}
                        data-testid={`delete-${idx}`}
                        variant="ghost"
                        className="p-2 hover:bg-red-500/10 hover:text-red-500"
                      >
                        <Trash2 size={22} className="hover:text-red-500" />
                      </Button>
                    </div>
                  </div>

                  <div className="text-gray-400 leading-relaxed text-lg line-clamp-3">
                    {devotional.content.substring(0, 300)}...
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}